import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { VodKind, VodShelf, VodShelfItem, VodShelfRow } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import type { Translator } from '../i18n/i18n.js';
import { PosterTile } from '../components/PosterTile.js';
import { scrollFocusedIntoView } from '../scrollFocusedIntoView.js';

/**
 * Film e Serie TV: la vetrina on demand (03-SCHERMATE-TV §6), nella forma del prototipo v3.
 *
 * Un titolo a 64 px, le pastiglie dei generi, e sotto le fasce orizzontali di locandine. Le fasce le
 * decide il dominio (`buildVodShelf`): quali sono, cosa contengono, e **quali non si disegnano
 * affatto** — una fascia «Consigliati per te» vuota è peggio di nessuna fascia, perché insegna che
 * quella riga è finta.
 *
 * ## Il cursore è (fascia, colonna), e le pastiglie sono la fascia zero
 *
 * Non fuoco spaziale: qui la geometria è regolare e un indice per asse è più prevedibile di una
 * misura. Le pastiglie dei generi stanno **dentro** il conteggio come fascia zero, per la stessa
 * ragione per cui il tasto «aggiorna» sta dentro quello del menu: un elemento che si vede e che il
 * telecomando non raggiunge è il difetto peggiore su un televisore — non c'è un puntatore con cui
 * aggirarlo.
 *
 * ## La colonna non si perde cambiando fascia
 *
 * Scendendo da una fascia di venti titoli a una di tre, la colonna si **accorcia** invece di
 * azzerarsi: chi era sul quinto titolo e scende si ritrova sull'ultimo della fascia sotto, non
 * teletrasportato all'inizio. Risalendo, la colonna di prima è persa ed è giusto così: ricordare una
 * colonna per fascia darebbe un fuoco che salta in un posto che chi guarda non ha scelto.
 */

export interface VodScreenProps {
  readonly focusPort: FocusPort;
  readonly kind: VodKind;
  readonly shelf: VodShelf;
  /** L'istante del server: serve a scrivere «aggiunto tre giorni fa», che dipende da adesso. */
  readonly serverNowMs: number;
  readonly onOpen: (contentId: string) => void;
  /** «Vedi tutti» e le pastiglie: riducono la vetrina a un genere. */
  readonly onOpenGenre: (genreId: string) => void;
  /**
   * Il genere su cui la vetrina è ridotta, o `null` per la vetrina intera.
   *
   * Quando c'è, il titolo grande è il **nome del genere**: non un titolo fisso con una nota accanto.
   * Chi arriva su una schermata di locandine deve sapere in un'occhiata dove si trova, e «Film» sopra
   * un elenco di soli thriller è la risposta a una domanda che nessuno ha fatto.
   */
  readonly genreFilter?: { readonly id: string; readonly name: string } | null;
  /** Indietro con un genere aperto: si torna alla vetrina intera invece di uscire. */
  readonly onClearGenre: () => void;
  /**
   * «Tutti i generi · N»: la pastiglia in fondo alla fila, che apre l'elenco completo.
   *
   * Non è una comodità. Le pastiglie sono **cinque**; su un'installazione vera le categorie sono
   * ottanta, con nomi lunghi — una fila orizzontale di cinque non solo è scomoda, **nasconde le
   * altre settantacinque**. Senza questa via, tre quarti del catalogo non si raggiungono.
   */
  readonly onOpenAllGenres?: () => void;
  /** Indietro: torna al menu. */
  readonly onRequestMenu: () => void;
  /** Vero quando i tasti sono del menu: qui non si muove niente. */
  readonly menuHasFocus: boolean;
  /** Vero mentre il catalogo sta arrivando: si dice, invece di mostrare una vetrina vuota. */
  readonly loading?: boolean;
  /** Il codice dell'errore, quando il catalogo non è arrivato affatto. */
  readonly errorCode?: string | null;
}

interface Cursor {
  readonly row: number;
  readonly col: number;
}

export function VodScreen({
  focusPort,
  kind,
  shelf,
  serverNowMs,
  onOpen,
  onOpenGenre,
  genreFilter = null,
  onClearGenre,
  onOpenAllGenres,
  onRequestMenu,
  menuHasFocus,
  loading = false,
  errorCode = null,
}: VodScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState<Cursor>({ row: 0, col: 0 });

  /*
    Le funzioni che arrivano da fuori si leggono da un riferimento.

    Stessa cura della diretta e dei preferiti: elencarle fra le dipendenze dell'effetto disfarebbe e
    rifarebbe la sottoscrizione al telecomando a ogni disegno — e il disegno qui lo provoca anche solo
    lo scorrimento di una fascia.
  */
  const handlers = useRef({ onOpen, onOpenGenre, onRequestMenu, onClearGenre, onOpenAllGenres });
  useEffect(() => {
    handlers.current = { onOpen, onOpenGenre, onRequestMenu, onClearGenre, onOpenAllGenres };
  }, [onOpen, onOpenGenre, onRequestMenu, onClearGenre, onOpenAllGenres]);

  /*
    Cambiando genere il cursore torna in testa.

    Restando dov'era, chi apre un genere da duecento titoli partendo dalla dodicesima colonna della
    fascia precedente si ritroverebbe a metà di un elenco che non ha ancora visto — e la prima cosa
    che vede non sarebbe la prima cosa dell'elenco.
  */
  useEffect(() => {
    setCursor({ row: 0, col: 0 });
  }, [genreFilter?.id]);

  /** Se l'ultima pastiglia della fila è «Tutti i generi»: entra nel conteggio come le altre. */
  const hasAllGenresChip = onOpenAllGenres !== undefined && genreFilter === null && shelf.genres.length > 0;

  /** Quante colonne ha ogni fascia, pastiglie comprese. È l'unica cosa che serve per muoversi. */
  const widths = useMemo(() => {
    const chipColumns = shelf.chips.length + (hasAllGenresChip ? 1 : 0);
    const rowColumns = shelf.rows.map((row) => row.items.length + (hasMore(row) ? 1 : 0));
    return [chipColumns, ...rowColumns];
  }, [shelf, hasAllGenresChip]);

  const move = useCallback(
    (deltaRow: number, deltaCol: number) => {
      setCursor((current) => {
        if (deltaRow !== 0) {
          /*
            Si salta la fascia **vuota**, non si atterra dentro.

            Le pastiglie possono non esserci (un catalogo senza categorie nominate), e una fascia con
            zero colonne è un fuoco invisibile: si scende finché non se ne trova una con qualcosa
            dentro, e se non c'è si resta dove si era.
          */
          let row = current.row;
          for (let step = 0; step < widths.length; step += 1) {
            row += deltaRow;
            if (row < 0 || row >= widths.length) return current;
            if ((widths[row] ?? 0) > 0) {
              return { row, col: Math.min(current.col, (widths[row] ?? 1) - 1) };
            }
          }
          return current;
        }
        const columns = widths[current.row] ?? 0;
        const col = current.col + deltaCol;
        if (col < 0 || col >= columns) return current;
        return { row: current.row, col };
      });
    },
    [widths],
  );

  const choose = useCallback(() => {
    if (cursor.row === 0) {
      const chip = shelf.chips[cursor.col];
      if (chip) {
        handlers.current.onOpenGenre(chip.id);
        return;
      }
      // Oltre l'ultimo genere c'è «Tutti i generi», che esiste solo quando qualcuno lo può aprire.
      if (hasAllGenresChip) handlers.current.onOpenAllGenres?.();
      return;
    }
    const row = shelf.rows[cursor.row - 1];
    if (!row) return;
    const item = row.items[cursor.col];
    if (item) {
      handlers.current.onOpen(item.item.id);
      return;
    }
    // Oltre l'ultimo titolo c'è «Vedi tutti», che esiste solo se la fascia è tagliata.
    if (hasMore(row) && row.genreId !== null) handlers.current.onOpenGenre(row.genreId);
  }, [cursor, shelf, hasAllGenresChip]);

  useEffect(() => {
    if (menuHasFocus) return undefined;
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        // Con un genere aperto, Indietro **risale di un livello**: alla vetrina intera. Mandare al
        // menu da qui obbligherebbe a rientrare nella sezione per vedere le altre fasce.
        if (genreFilter !== null) handlers.current.onClearGenre();
        else handlers.current.onRequestMenu();
        return true;
      }
      if (key.kind === 'select') {
        choose();
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'left') move(0, -1);
      if (key.direction === 'right') move(0, 1);
      if (key.direction === 'up') move(-1, 0);
      if (key.direction === 'down') move(1, 0);
      return true;
    });
  }, [focusPort, menuHasFocus, move, choose, genreFilter]);

  /*
    Il fuoco si porta **dentro lo schermo**.

    Il cursore è un indice e non il fuoco del browser, quindi nessuno scorre da sé: una fascia più
    larga dello schermo lascerebbe il cursore su una locandina che non si vede, e la navigazione
    diventerebbe indovinare. `block: 'nearest'` scorre il minimo necessario — con `center` la vetrina
    salterebbe a ogni passo laterale.
  */
  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const focused = frameRef.current?.querySelector<HTMLElement>('[data-focused="1"]');
    scrollFocusedIntoView(focused);
  }, [cursor, shelf, hasAllGenresChip]);

  const sectionTitle = translator.t(kind === 'movie' ? 'vod.movies.title' : 'vod.series.title');
  const title = genreFilter?.name ?? sectionTitle;

  /*
    Quanti titoli ha il genere aperto, oppure `null` quando non si è dentro un genere.

    La vista di un genere ha **una fascia sola**, e quella fascia si chiama come il genere: il nome
    finiva scritto due volte, a otto pixel di distanza. Qui si prende il conteggio dalla fascia per
    portarlo accanto al titolo, e l'intestazione della fascia non si disegna più.
  */
  const genreRow = genreFilter === null ? null : shelf.rows.find((row) => row.kind === 'genre');
  const genreCount = genreRow?.kind === 'genre' ? genreRow.total : null;

  return (
    <div
      ref={frameRef}
      data-vod={kind}
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '56px 0 80px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ padding: '0 72px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {genreFilter !== null && (
          // La sezione da cui si è arrivati, sopra il nome del genere: è l'unico modo di sapere se
          // «Thriller» sono i film o le serie, e il tasto Indietro non lo dice.
          <span
            data-vod-breadcrumb="1"
            style={{
              font: `700 22px ${FONT_STACK}`,
              color: colors.accent,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
            }}
          >
            {sectionTitle}
          </span>
        )}
        <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>{title}</h1>
        {/*
          Quanti titoli, **qui** e non sulla fascia sotto.

          Entrando in un genere il nome si leggeva due volte: una nel titolo grande e una come nome
          della fascia sotto, che in questa vista è **la stessa cosa** — c'è una fascia sola, ed è il
          genere. Il conteggio invece dice qualcosa che il titolo non dice, quindi resta; il nome
          ripetuto se ne va, insieme all'intestazione della fascia.
        */}
        {genreCount !== null && (
          <span data-vod-genre-count="1" style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
            {translator.t('vod.titles', { count: genreCount })}
          </span>
        )}
      </div>

      {errorCode !== null ? (
        /*
          Il catalogo non è arrivato, e non c'era nemmeno una copia sull'apparecchio.

          Si dice, con il codice: una vetrina vuota sarebbe indistinguibile da un catalogo senza film,
          cioè da un problema di abbonamento — e chi chiama l'assistenza racconterebbe la cosa
          sbagliata.
        */
        <p data-vod-error="1" style={{ padding: '40px 72px', font: `600 30px ${FONT_STACK}`, color: colors.errorText }}>
          {translator.t('vod.failed')}
          <span style={{ display: 'block', font: `500 20px ${FONT_STACK}`, color: colors.textFaint, paddingTop: 8 }}>
            {errorCode}
          </span>
        </p>
      ) : loading ? (
        <p data-vod-loading="1" style={{ padding: '40px 72px', font: `600 30px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('vod.loading')}
        </p>
      ) : shelf.rows.length === 0 ? (
        <p data-vod-empty="1" style={{ padding: '40px 72px', font: `600 30px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t(kind === 'movie' ? 'vod.movies.none' : 'vod.series.none')}
        </p>
      ) : (
        <>
          {(shelf.chips.length > 0 || hasAllGenresChip) && (
            <>
              <div style={{ padding: '24px 72px 0' }}>
                {/*
                  «I tuoi generi» oppure «Titoli A–Z», secondo come il catalogo è raggruppato.

                  Scrivere «I tuoi generi» sopra una fila di lettere sarebbe una bugia piccola e
                  visibile: chi legge capisce subito che l'app non sa cosa sta mostrando.
                */}
                <span style={sectionLabelStyle}>
                  {translator.t(shelf.grouping === 'initial' ? 'vod.alphabet' : 'vod.yourGenres')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', scrollbarWidth: 'none', padding: '14px 72px 12px' }}>
                {shelf.chips.map((chip, index) => {
                  const focused = cursor.row === 0 && cursor.col === index;
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      data-f="1"
                      data-genre-chip={chip.id}
                      data-focused={focused ? '1' : '0'}
                      onClick={() => handlers.current.onOpenGenre(chip.id)}
                      style={{
                        /*
                          Una pastiglia si allarga fino a un tetto, poi **va a capo**.

                          I nomi veri arrivano a «Cinema Erotico – XXX VOD (solo per adulti)»: su una
                          riga sola una pastiglia così occupa mezzo schermo e spinge le altre quattro
                          fuori dal bordo, dove non si sa che ci sono. Il tetto tiene la fila
                          leggibile; le due righe fanno stare il nome intero senza tagliarlo, che su
                          questi nomi conta — la parte che li distingue è quasi sempre in fondo.

                          `minHeight` e non `height`: con l'altezza fissa la seconda riga uscirebbe
                          dalla pastiglia invece di allargarla.
                        */
                        flex: 'none',
                        minHeight: 60,
                        maxWidth: 420,
                        padding: '10px 28px',
                        borderRadius: 30,
                        border: `2px solid ${colors.surfaceRaised}`,
                        background: colors.surfaceRaised,
                        color: colors.text,
                        font: `600 23px/1.2 ${FONT_STACK}`,
                        textAlign: 'center',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        cursor: 'pointer',
                        outline: focused ? `4px solid ${colors.accent}` : 'none',
                        outlineOffset: 4,
                        transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
                        transition: 'var(--tr, transform .16s ease)',
                      }}
                    >
                      {chip.name}
                    </button>
                  );
                })}
                {hasAllGenresChip && (
                  <button
                    type="button"
                    data-f="1"
                    data-all-genres="1"
                    data-focused={cursor.row === 0 && cursor.col === shelf.chips.length ? '1' : '0'}
                    onClick={() => handlers.current.onOpenAllGenres?.()}
                    style={{
                      // `minHeight` come le altre: sono sulla stessa fila, e con un'altezza fissa
                      // questa resterebbe bassa accanto a una pastiglia andata a capo.
                      flex: 'none',
                      minHeight: 60,
                      padding: '10px 28px',
                      borderRadius: 30,
                      // Ambra e vuota, come nel disegno: non è un genere, è la via verso tutti.
                      border: `2px solid ${colors.accent}`,
                      background: 'transparent',
                      color: colors.accent,
                      font: `600 23px ${FONT_STACK}`,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      outline:
                        cursor.row === 0 && cursor.col === shelf.chips.length
                          ? `4px solid ${colors.accent}`
                          : 'none',
                      outlineOffset: 4,
                      transform:
                        cursor.row === 0 && cursor.col === shelf.chips.length
                          ? 'scale(var(--fs, 1.04))'
                          : 'none',
                      transition: 'var(--tr, transform .16s ease)',
                    }}
                  >
                    {translator.t('vod.allGenres', { count: shelf.genres.length })}
                  </button>
                )}
              </div>
            </>
          )}

          {shelf.rows.map((row, rowIndex) => (
            <section key={rowKey(row)} style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 16 }}>
              {/*
                L'intestazione si salta quando dice quel che dice già il titolo grande: è il caso
                della vista di un genere, dove la fascia **è** il genere. Altrove serve, perché le
                fasce sono più d'una e vanno distinte.
              */}
              {!(genreFilter !== null && row.kind === 'genre') && (
                <div style={{ padding: '0 72px', display: 'flex', alignItems: 'baseline', gap: 18 }}>
                  <span style={{ font: `700 32px ${FONT_STACK}` }}>{rowTitle(row, translator)}</span>
                  <span style={{ font: `500 22px ${FONT_STACK}`, color: colors.textDim }}>
                    {rowSubtitle(row, translator)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 24, overflowX: 'auto', scrollbarWidth: 'none', padding: '14px 72px 18px' }}>
                {row.items.map((entry, colIndex) => (
                  <PosterTile
                    key={entry.item.id}
                    title={entry.item.title}
                    subtitle={subtitleOf(entry, translator)}
                    posterUrl={entry.item.posterUrl}
                    percent={entry.state.percent}
                    watched={entry.state.watched}
                    lockedRating={null}
                    note={noteOf(row, entry, serverNowMs, translator)}
                    focused={cursor.row === rowIndex + 1 && cursor.col === colIndex}
                    onSelect={() => handlers.current.onOpen(entry.item.id)}
                  />
                ))}
                {hasMore(row) && row.genreId !== null && (
                  <button
                    type="button"
                    data-f="1"
                    data-see-all={row.genreId}
                    data-focused={cursor.row === rowIndex + 1 && cursor.col === row.items.length ? '1' : '0'}
                    onClick={() => handlers.current.onOpenGenre(row.genreId as string)}
                    style={{
                      flex: 'none',
                      width: 230,
                      height: 345,
                      border: 0,
                      borderRadius: 14,
                      background: colors.surfaceRaised,
                      color: colors.text,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      outline:
                        cursor.row === rowIndex + 1 && cursor.col === row.items.length
                          ? `4px solid ${colors.accent}`
                          : 'none',
                      outlineOffset: 4,
                      transition: 'var(--tr, transform .16s ease)',
                    }}
                  >
                    <span style={{ font: `700 26px ${FONT_STACK}` }}>{translator.t('vod.seeAll')}</span>
                    <span style={{ font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
                      {translator.t('vod.titles', { count: row.total })}
                    </span>
                  </button>
                )}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

const sectionLabelStyle = {
  font: `600 20px ${FONT_STACK}`,
  color: colors.textDim,
  letterSpacing: '.06em',
  textTransform: 'uppercase' as const,
};

function hasMore(row: VodShelfRow): boolean {
  return row.genreId !== null && row.total > row.items.length;
}

/** Due fasce di genere diverse hanno lo stesso `kind`: la chiave è il genere quando c'è. */
function rowKey(row: VodShelfRow): string {
  return row.genreId ?? row.kind;
}

/**
 * Il titolo di una fascia.
 *
 * Esportata perché la usa anche la vetrina del telefono: due funzioni che nominano le stesse fasce
 * divergono alla prima aggiunta, e si vedrebbe solo notando che i due apparecchi chiamano la stessa
 * riga in due modi.
 */
export function rowTitle(row: VodShelfRow, translator: Translator): string {
  // Le fasce di genere portano il nome della categoria, che **non si traduce**: arriva dal catalogo
  // dell'operatore (14-LINGUE-E-TESTI §1).
  if (row.kind === 'genre') return row.genreName;
  return translator.t(`vod.row.${row.kind}`);
}

function rowSubtitle(row: VodShelfRow, translator: Translator): string {
  if (row.kind === 'genre') return translator.t('vod.titles', { count: row.total });
  if (row.kind === 'recommended') return translator.t('vod.row.recommended.sub');
  if (row.kind === 'latest') return translator.t('vod.row.latest.sub');
  return '';
}

/**
 * La riga grigia sotto il titolo: genere e anno, quel che c'è dei due.
 *
 * Quando non c'è nessuno dei due resta il tipo («Film», «Serie TV»), che è poco ma è vero: la riga
 * vuota farebbe sembrare la piastrella incompleta, e inventare un genere sarebbe peggio.
 */
function subtitleOf(entry: VodShelfItem, translator: Translator): string {
  const parts: string[] = [];
  if (entry.item.genre !== '') parts.push(entry.item.genre);
  if (entry.item.year > 0) parts.push(String(entry.item.year));
  if (parts.length === 0) {
    return translator.t(entry.item.kind === 'movie' ? 'vod.kind.movie' : 'vod.kind.series');
  }
  return parts.join(' · ');
}

/**
 * La nota ambra: il perché del consiglio, o da quanto è in catalogo.
 *
 * I giorni si contano **qui**, contro l'istante del server, e non si archiviano: un numero di giorni
 * salvato in locale invecchia da solo, ed è la stessa trappola delle finestre di prova che scadono
 * col calendario.
 */
function noteOf(
  row: VodShelfRow,
  entry: VodShelfItem,
  nowMs: number,
  translator: Translator,
): string | null {
  if (row.kind === 'recommended' && entry.because !== null && entry.because !== '') {
    return translator.t('vod.because', { title: entry.because });
  }
  if (row.kind === 'latest' && entry.item.addedAtMs !== null) {
    const days = Math.floor((nowMs - entry.item.addedAtMs) / 86_400_000);
    if (days <= 0) return translator.t('vod.addedToday');
    return translator.t('vod.addedDaysAgo', { days });
  }
  return null;
}
